import React from 'react';

/**
 * Utility functions for managing body scroll when dialogs are open
 * Prevents page scrolling and maintains scroll position
 */

declare global {
  interface Document {
    primeDialogParams?: Array<{ id: string; hasBlockScroll: boolean }>;
  }
}

let scrollPosition = 0;
let isScrollLocked = false;

const clearBodyScrollInlineStyles = (): void => {
  document.body.style.removeProperty('overflow');
  document.body.style.removeProperty('padding-right');
  document.body.style.removeProperty('--scrollbar-width');
};

/**
 * Clears stale scroll-lock state left by profile dialogs, loaders, or PrimeReact.
 * Call after logout or before opening auth modals so `blockScroll` can work again.
 */
export const resetDocumentScrollLock = (): void => {
  clearBodyScrollInlineStyles();
  document.body.classList.remove('p-overflow-hidden');
  document.body.removeAttribute('data-scroll-position');
  document.body.removeAttribute('data-virtup-body-height');
  document.body.removeAttribute('data-virtup-scroll-y');

  if (document.primeDialogParams) {
    document.primeDialogParams = [];
  }

  isScrollLocked = false;
};

/**
 * Removes inline overflow overrides so PrimeReact Dialog `blockScroll` can apply.
 */
export const prepareDocumentScrollLockForModal = (): void => {
  document.body.style.removeProperty('overflow');
};

/**
 * Closes page-level PrimeReact overlays (e.g. search filter MultiSelect) so they
 * do not stack above auth modals when login/signup opens.
 */
export const dismissOpenPageOverlayPanels = (): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const activeElement = document.activeElement as HTMLElement | null;
  if (activeElement?.closest('.p-multiselect, .p-dropdown, .p-autocomplete')) {
    activeElement.blur();
  }

  document.querySelectorAll<HTMLElement>(
    '.p-multiselect.p-focus, .p-dropdown.p-focus, .p-autocomplete.p-focus, .p-multiselect.p-inputwrapper-focus, .p-dropdown.p-inputwrapper-focus, .p-autocomplete.p-inputwrapper-focus'
  ).forEach((el) => {
    el.blur();
  });
};

/**
 * Locks body scroll and saves current scroll position
 * Prevents page from scrolling to top when dialogs open
 */
export const lockBodyScroll = (): void => {
  if (isScrollLocked) {
    return;
  }
  
  // Save current scroll position
  scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
  
  // Simple approach - just disable body scroll
  document.body.style.overflow = 'hidden';
  
  // Store the current scroll position in a data attribute for reference
  document.body.setAttribute('data-scroll-position', scrollPosition.toString());
  
  isScrollLocked = true;
};

/**
 * Unlocks body scroll and restores previous scroll position
 */
export const unlockBodyScroll = (): void => {
  if (!isScrollLocked) {
    if (
      document.body.style.position === 'fixed' ||
      document.body.style.height ||
      document.body.getAttribute('data-scroll-position')
    ) {
      clearBodyScrollInlineStyles();
      document.body.removeAttribute('data-scroll-position');
    }
    return;
  }
  
  clearBodyScrollInlineStyles();
  document.body.removeAttribute('data-scroll-position');
  window.scrollTo(0, scrollPosition);
  isScrollLocked = false;
};

/**
 * Hook to manage scroll lock for dialogs
 * Automatically locks/unlocks scroll based on visibility
 */
export const useScrollLock = (isVisible: boolean): void => {
  React.useEffect(() => {
    // console.log('🎣 useScrollLock effect triggered, isVisible:', isVisible);
    
    if (isVisible) {
      // console.log('👁️ Dialog is visible, locking scroll');
      // Apply scroll lock immediately
      lockBodyScroll();
    } else {
      // console.log('👁️ Dialog is hidden, unlocking scroll');
      unlockBodyScroll();
    }
    
    // Cleanup function to ensure scroll is unlocked when component unmounts
    return () => {
      // console.log('🧹 useScrollLock cleanup - unlocking scroll');
      unlockBodyScroll();
    };
  }, [isVisible]);
};

/**
 * Pre-lock scroll before dialog opens
 * Call this function before setting dialog visible to true
 */
export const preLockBodyScroll = (): void => {
  // console.log('🚀 preLockBodyScroll called - preventing scroll jump before dialog opens');
  
  // Save current scroll position immediately
  scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
  // console.log('📍 Pre-lock scroll position saved:', scrollPosition);
  
  // Apply the lock immediately to prevent any scroll jump
  lockBodyScroll();
};

/**
 * Initialize scroll lock on component mount
 * This prevents scroll jump from the very beginning
 */
export const initializeScrollLock = (): void => {
  // console.log('🎯 initializeScrollLock called - setting up scroll prevention');
  
  // Save current scroll position
  scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
  // console.log('📍 Initial scroll position saved:', scrollPosition);
  
  // Simple initialization - no complex CSS needed
  // console.log('🌍 Scroll lock initialized with simple approach');
};

/**
 * Check if scroll is currently locked
 */
export const isScrollCurrentlyLocked = (): boolean => {
  return isScrollLocked;
};

/**
 * Force unlock scroll (emergency cleanup)
 * Use this if you need to force unlock scroll regardless of state
 */
export const forceUnlockScroll = (): void => {
  resetDocumentScrollLock();
};
